import { Combobox } from '@headlessui/react';
import { cn } from 'cn';
import { useRef, useState } from 'react';
import { useTheme } from '#ui/hooks/useTheme';
import { ComboboxError } from './private/ComboboxError';
import { InputComboboxField } from './private/ComboboxField';
import { InputComboboxOptions } from './private/ComboboxOptions';
import { useComboboxPositioning } from './private/useComboboxPositioning';
import { useComboboxSelection } from './private/useComboboxSelection';
import { useInputComboboxController } from './private/useInputComboboxController';
import {
  filterComboboxOptions,
  getComboboxChoiceLimit,
  resolveComboboxDefaultValue,
} from './private/utils';

export type Option<TValue> = {
  label: string;
  description?: string | React.ReactNode;
  value: TValue;
  disabled?: boolean;
};

type InputComboboxBaseProps<TValue = string | number> = {
  name: string;
  label: string;
  labelDescription?: string | React.ReactNode;
  labelClassName?: string;
  tooltip?: string;
  placeholder?: string;
  options?: Option<TValue>[];
  optionsClassName?: string;
  disabled?: boolean;
  required?: boolean | string;
  deps?: string | string[];
  optionsMaxHeight?: number;
  className?: string;
};

type InputComboboxSingleProps<TValue = string | number> =
  InputComboboxBaseProps<TValue> & {
    multiple?: false;
    defaultValue?: TValue;
    onChange?: (option: Option<TValue> | undefined) => void;
  };

type InputComboboxMultipleProps<TValue = string | number> =
  InputComboboxBaseProps<TValue> & {
    multiple: true;
    choiceLimit?: number;
    defaultValue?: TValue[];
    onChange?: (options: Option<TValue>[] | undefined) => void;
  };

export type InputComboboxProps<TValue = string | number> =
  | InputComboboxSingleProps<TValue>
  | InputComboboxMultipleProps<TValue>;

function InputCombobox<T>(props: InputComboboxProps<T>) {
  const {
    name,
    label,
    labelDescription,
    labelClassName,
    tooltip,
    placeholder,
    options,
    optionsClassName,
    required,
    deps,
    defaultValue,
    optionsMaxHeight,
    className,
  } = props;
  const isMultiple = props.multiple === true;
  const choiceLimit = getComboboxChoiceLimit(props);
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { refs, floatingStyles } = useComboboxPositioning();
  const filteredOptions = filterComboboxOptions(options, query);
  const resolvedDefaultValue = resolveComboboxDefaultValue(
    defaultValue,
    isMultiple,
  );
  const { disabled, error, errorId, field, inputId } =
    useInputComboboxController({
      defaultValue: resolvedDefaultValue,
      deps,
      disabled: props.disabled,
      name,
      required,
    });

  const {
    handleBackspace,
    handleChange,
    handleRemove,
    selectedOptions,
    selectedValue,
    selectedValues,
  } = useComboboxSelection({
    choiceLimit,
    closeButtonRef,
    disabled,
    field,
    isMultiple,
    options,
    props,
    query,
    setQuery,
  });

  return (
    <div className={className}>
      <Combobox
        as={'div'}
        className={cn('relative', 'kunde:flex kunde:gap-y-3 kunde:flex-col')}
        value={isMultiple ? selectedValues : selectedValue}
        onChange={handleChange}
        disabled={disabled}
        onBlur={field.onBlur}
        onBlurCapture={field.onBlur}
        immediate
        multiple={isMultiple}
      >
        {({ open }) => (
          <>
            <InputComboboxField
              closeButtonRef={closeButtonRef}
              errorId={errorId}
              hasError={!!error}
              inputId={inputId}
              isOpen={open}
              label={label}
              labelClassName={labelClassName}
              labelDescription={labelDescription}
              name={name}
              onQueryChange={setQuery}
              options={options}
              placeholder={placeholder}
              multiField={
                isMultiple
                  ? {
                      disabled: !!disabled,
                      errorId,
                      hasError: !!error,
                      inputId,
                      name,
                      onBackspace: handleBackspace,
                      onQueryChange: setQuery,
                      onRemove: handleRemove,
                      placeholder,
                      query,
                      selectedOptions,
                    }
                  : undefined
              }
              setReference={refs.setReference}
              tooltip={tooltip}
            />
            <InputComboboxOptions
              choiceLimit={choiceLimit}
              floatingStyles={floatingStyles}
              isMultiple={isMultiple}
              options={filteredOptions}
              optionsClassName={optionsClassName}
              optionsMaxHeight={optionsMaxHeight}
              selectedValues={selectedValues}
              setFloating={refs.setFloating}
              theme={theme}
            />
          </>
        )}
      </Combobox>
      <ComboboxError error={error} errorId={errorId} />
    </div>
  );
}

export default InputCombobox;
